import { useEffect, useRef, useState } from 'react';

const CINEMATIC_BG_VIDEO = '/manus-storage/hero-tech-3d_f7444ffe.mp4';

/**
 * Shared visual atmosphere for non-hero routes.
 * The fixed video keeps the site visually alive while the scroll-linked
 * transform gives the background a subtle sense of depth without competing
 * with page copy or controls.
 */
export default function CinematicScrollBackground() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<number | null>(null);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncPreferences = () => {
      setIsReducedMotion(motionQuery.matches);
      setIsMobile(window.innerWidth < 768);
    };

    syncPreferences();
    motionQuery.addEventListener?.('change', syncPreferences);
    window.addEventListener('resize', syncPreferences, { passive: true });

    return () => {
      motionQuery.removeEventListener?.('change', syncPreferences);
      window.removeEventListener('resize', syncPreferences);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || isReducedMotion) return;

    const updateDepth = () => {
      const scrollProgress = Math.min(window.scrollY / Math.max(document.documentElement.scrollHeight - window.innerHeight, 1), 1);
      const y = -20 + scrollProgress * 42;
      const scale = 1.08 + scrollProgress * 0.04;
      video.style.transform = `translate3d(0, ${y}px, 0) scale(${scale})`;
      frameRef.current = null;
    };

    const onScroll = () => {
      if (frameRef.current === null) frameRef.current = requestAnimationFrame(updateDepth);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    updateDepth();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [isReducedMotion]);

  const showVideo = !isReducedMotion;
  const mobileOpacity = isMobile ? 0.16 : 0.28;

  return (
    <div className="cinematic-site-background" aria-hidden="true">
      <div className="cinematic-site-fallback" />
      {showVideo && (
        <video
          ref={videoRef}
          className="cinematic-site-video"
          src={CINEMATIC_BG_VIDEO}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedData={() => setReady(true)}
          style={{ opacity: ready ? mobileOpacity : 0 }}
        />
      )}
      <div className="cinematic-site-tint" />
      <div className="cinematic-site-light" />
    </div>
  );
}
